import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import Box from "@mui/material/Box";
import Grid2 from "@mui/material/Grid2";
import FormInput from "../Common/FormInputs/MuiTextField";
import {
  InfoPairLabel,
  InfoPairValue,
  LabelText,
  SectionSubHeading,
} from "../Common/Typography/AppTypography";
import Dialog from "../Common/Dailog/Dailog";
import { setAlert } from "../../action/alert";
import { deleteAsset, getAssetErrorMessage, getAssetMetadata, groupResolutionsByTag } from "../../action/asset";
import customConfig from "../../config";
import { selectMenuProps } from "../../utils/selectMenuProps";
import { useDesignTokens } from "../../theme/useDesignTokens";
import { ASSET_RESOLUTION_OPTIONS } from "./assetConstants";

function AssetDeleteForm({ open, assetId = "", tags = [], onClose, onDeleted }) {
  const dispatch = useDispatch();
  const { colors } = useDesignTokens();
  const [tag, setTag] = useState("");
  const [resolution, setResolution] = useState("");
  const [serverTags, setServerTags] = useState([]);
  const [confirming, setConfirming] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const assetIdValue = assetId?.trim();
    setTag("");
    setResolution("");
    setServerTags([]);
    setConfirming(false);
    setLoading(false);

    if (!assetIdValue) {
      return undefined;
    }

    let cancelled = false;

    const loadTags = async () => {
      try {
        const res = await getAssetMetadata(assetIdValue);
        const entries = groupResolutionsByTag(res?.data)
          .filter((entry) => entry.tag && entry.resolutions?.length)
          .map((entry) => ({ tag: entry.tag, resolutions: entry.resolutions }));

        if (!cancelled) {
          setServerTags(entries);
        }
      } catch {
        if (!cancelled) {
          const fallback = (Array.isArray(tags) ? tags : []).filter(
            (entry) => entry?.tag && entry.resolutions?.length
          );
          setServerTags(fallback);
        }
      }
    };

    loadTags();

    return () => {
      cancelled = true;
    };
  }, [open, assetId, tags]);

  const trimmedAssetId = assetId?.trim() || "";
  const trimmedTag = tag.trim();
  const fullDelete = !trimmedTag && !resolution;
  const assetTags = serverTags.filter((entry) => entry?.tag && entry.resolutions?.length);
  const tagOptions = [
    { value: "", label: "All tags" },
    ...assetTags.map((entry) => ({ value: entry.tag, label: entry.tag })),
  ];
  const selectedTag = assetTags.find((entry) => entry.tag === trimmedTag);
  const resolutionSource = selectedTag ? [selectedTag] : assetTags;
  const resolutionChoices = [...new Set(resolutionSource.flatMap((entry) => entry.resolutions || []))].map(
    (value) => ({
      value,
      label: ASSET_RESOLUTION_OPTIONS.find((option) => option.value === value)?.label || value,
    })
  );
  const resolutionOptions = [{ value: "", label: "All resolutions" }, ...resolutionChoices];

  const handleClose = () => {
    if (!loading) {
      onClose?.();
    }
  };

  const deleteScopeText = () => {
    if (resolution) {
      return `This deletes the ${resolution} image. Other resolutions stay.`;
    }

    if (trimmedTag) {
      return `This deletes files stored with the tag ${trimmedTag}.`;
    }

    return "No tag or resolution is selected. This deletes the asset and all of its resolutions.";
  };

  const handleContinue = () => {
    if (!trimmedAssetId) {
      dispatch(setAlert("Asset ID Cannot be Empty", "danger"));
      return;
    }

    setConfirming(true);
  };

  const handleDeleteConfirm = async () => {
    setLoading(true);

    try {
      await deleteAsset(trimmedAssetId, trimmedTag, resolution || undefined);
      dispatch(setAlert(customConfig.DELETE_SUCCESS, "success"));
      onDeleted?.({ assetId: trimmedAssetId, fullDelete });
    } catch (err) {
      dispatch(
        setAlert(
          err?.response?.data?.message ||
            getAssetErrorMessage(err, "Request failed", "Asset not found"),
          "danger"
        )
      );
      setConfirming(false);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      title="Delete Asset"
      maxWidth="sm"
      showCloseButton
      actions={
        confirming
          ? [
              {
                label: "Cancel",
                variant: "ghost",
                onClick: handleClose,
                disabled: loading,
              },
              {
                label: "Delete",
                tooltip: "Click to delete the asset.",
                variant: "danger",
                onClick: handleDeleteConfirm,
                loading,
              },
            ]
          : [
              {
                label: "Cancel",
                variant: "ghost",
                onClick: handleClose,
                disabled: loading,
              },
              {
                label: "Delete Asset",
                variant: "danger",
                onClick: handleContinue,
                disabled: loading,
              },
            ]
      }
    >
      {confirming ? (
        <Box
          sx={{
            bgcolor: colors?.disabled?.bg,
            p: 3,
            borderRadius: 2,
            textAlign: "left",
          }}
        >
          <SectionSubHeading sx={{ mb: 2 }}>
            Are you sure you want to delete this asset?
          </SectionSubHeading>
          <SectionSubHeading sx={{ mb: 2 }}>{deleteScopeText()}</SectionSubHeading>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <Box>
              <InfoPairLabel>Asset ID:</InfoPairLabel>
              <InfoPairValue sx={{ wordBreak: "break-all" }}>
                {trimmedAssetId}
              </InfoPairValue>
            </Box>
            <Box>
              <InfoPairLabel>Tag:</InfoPairLabel>
              <InfoPairValue>{trimmedTag || "All tags"}</InfoPairValue>
            </Box>
            <Box>
              <InfoPairLabel>Resolution:</InfoPairLabel>
              <InfoPairValue>{resolution || "All resolutions"}</InfoPairValue>
            </Box>
          </Box>
        </Box>
      ) : (
        <Box>
          <Grid2 container spacing={2} sx={{ mb: 2, alignItems: "flex-start" }}>
            <Grid2 size={{ xs: 12, md: 3 }} sx={{ pt: { md: 1.25 } }}>
              <LabelText sx={{ fontWeight: 600 }}>Asset ID:</LabelText>
            </Grid2>
            <Grid2 size={{ xs: 12, md: 9 }} sx={{ minWidth: 0 }}>
              <FormInput
                type="text"
                size="small"
                fullWidth
                value={trimmedAssetId}
                disabled
                sx={{ width: "100%", maxWidth: "100%", minWidth: 0 }}
              />
            </Grid2>
          </Grid2>

          <Grid2 container spacing={2} sx={{ mb: 2, alignItems: "flex-start" }}>
            <Grid2 size={{ xs: 12, md: 3 }} sx={{ pt: { md: 1.25 } }}>
              <LabelText sx={{ fontWeight: 600 }}>Tag:</LabelText>
            </Grid2>
            <Grid2 size={{ xs: 12, md: 9 }} sx={{ minWidth: 0 }}>
              <FormInput
                type={assetTags.length ? "select" : "text"}
                size="small"
                fullWidth
                placeholder="Optional"
                value={tag}
                onChange={(e) => {
                  const nextTag = e.target.value;
                  const nextEntry = assetTags.find((entry) => entry.tag === nextTag);
                  const allowed = nextEntry
                    ? nextEntry.resolutions || []
                    : assetTags.flatMap((entry) => entry.resolutions || []);
                  setTag(nextTag);
                  setResolution((current) => (allowed.includes(current) ? current : ""));
                }}
                options={tagOptions}
                displayEmpty
                renderValue={(value) =>
                  tagOptions.find((option) => option.value === value)?.label || value || "All tags"
                }
                MenuProps={selectMenuProps}
                helperText="Optional"
                sx={{ width: "100%", maxWidth: "100%", minWidth: 0 }}
              />
            </Grid2>
          </Grid2>

          <Grid2 container spacing={2} sx={{ alignItems: "flex-start" }}>
            <Grid2 size={{ xs: 12, md: 3 }} sx={{ pt: { md: 1.25 } }}>
              <LabelText sx={{ fontWeight: 600 }}>Resolution:</LabelText>
            </Grid2>
            <Grid2 size={{ xs: 12, md: 9 }} sx={{ minWidth: 0 }}>
              <FormInput
                type="select"
                size="small"
                fullWidth
                value={resolution}
                onChange={(e) => setResolution(e.target.value)}
                options={resolutionOptions}
                displayEmpty
                renderValue={(value) =>
                  resolutionOptions.find((option) => option.value === value)?.label ||
                  "All resolutions"
                }
                MenuProps={selectMenuProps}
                helperText="Optional. Each resolution has one image."
                sx={{ width: "100%", maxWidth: "100%", minWidth: 0 }}
              />
            </Grid2>
          </Grid2>
        </Box>
      )}
    </Dialog>
  );
}

export default AssetDeleteForm;
